import React from 'react';
import { makeStyles } from '@material-ui/core';
import {
  ScriptStepInternalNoteLinkSegment,
  ScriptStepInternalNoteSegment,
  ScriptStepInternalNoteSegmentTypes,
  ScriptStepInternalNoteTextSegment,
} from 'typings';
import { isValidWebUrl } from 'utils/functions';

interface ScriptStepInternalNoteProps {
  segments: ScriptStepInternalNoteSegment[];
}

const useStyles = makeStyles((theme) => ({
  container: {
    borderBottom:
      theme.palette.type === 'light'
        ? `1px solid rgba(0, 0, 0, 0.1)`
        : `1px solid rgba(255, 255, 255, 0.1)`,
    fontStyle: 'italic',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  text: {
    whiteSpace: 'pre-wrap',
  },
}));

const ScriptStepInternalNote = ({ segments }: ScriptStepInternalNoteProps) => {
  const styles = useStyles();

  const openCalendlyWidget = (e: any, url: string) => {
    // attempt to open the Calendly widget if the Calendly script is loaded
    if ('Calendly' in window) {
      const Calendly = window.Calendly as any;

      Calendly.initPopupWidget({
        url,
      });

      e.preventDefault();
    }
  };

  const renderTextSegment = (segment: ScriptStepInternalNoteTextSegment, index: number) => {
    return (
      <span key={index} className={styles.text}>
        {segment.text}
      </span>
    );
  };

  const renderLinkSegment = (segment: ScriptStepInternalNoteLinkSegment, index: number) => {
    const { text, url } = segment as ScriptStepInternalNoteLinkSegment;

    // check for valid URL before continuing
    if (!isValidWebUrl(url)) return null;

    // Calendly URLs can use the widget, but fall back to opening in a new tab if the widget can't open
    const calendlyDomain = 'https://calendly.com/';
    if (url.startsWith(calendlyDomain) && url.length > calendlyDomain.length) {
      return (
        <a
          key={index}
          className={styles.text}
          href={url}
          onClick={(e) => openCalendlyWidget(e, url)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {text}
        </a>
      );
    }

    // all other URLs open in a new tab
    return (
      <a key={index} className={styles.text} href={url} target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    );
  };

  const renderSegment = (segment: ScriptStepInternalNoteSegment, index: number) => {
    switch (segment.type) {
      case ScriptStepInternalNoteSegmentTypes.TEXT:
        return renderTextSegment(segment as ScriptStepInternalNoteTextSegment, index);

      case ScriptStepInternalNoteSegmentTypes.LINK:
        return renderLinkSegment(segment as ScriptStepInternalNoteLinkSegment, index);

      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      {segments.map((segment, index) => renderSegment(segment, index))}
    </div>
  );
};

export default ScriptStepInternalNote;
